import Axios from "../config/axios-config";
import { apiErrorCode, apiErrorMessage } from "../utils/apiErrorMessage";

// ─────────────────────────────────────────────────────────────────────────────
// Pop-ups para comercios (microservice-contracts, `contracts/announcements`).
//
// Dos públicos:
//   - el portal del COMERCIO: `getActiveAnnouncements` y `sendAnnouncementReceipt`.
//     Nunca deben romper ni demorar la sesión: timeout corto y, ante cualquier
//     error, "no hay anuncios".
//   - los AUTORES (admin / superadmin logístico, superadmin del sistema): el
//     resto. Devuelven `{ ok, data, error, code }` para que el editor muestre el
//     motivo que da el backend (`code` estable, `error` legible).
// ─────────────────────────────────────────────────────────────────────────────

const BASE = "contracts/announcements";

// El portal no espera más que esto: sin respuesta, se sigue sin anuncio.
const PORTAL_TIMEOUT_MS = 5000;

async function authorCall(request, fallback) {
  try {
    const response = await request();
    return { ok: true, data: response.data, error: null, code: null };
  } catch (err) {
    return {
      ok: false,
      data: null,
      error: apiErrorMessage(err, fallback),
      code: apiErrorCode(err),
    };
  }
}

// ── Portal del comercio ──────────────────────────────────────────────────────

/** Anuncios vigentes para el comercio de la sesión. Siempre resuelve: [] ante error. */
export async function getActiveAnnouncements() {
  try {
    const { data } = await Axios.get(`${BASE}/active`, { timeout: PORTAL_TIMEOUT_MS });
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/**
 * Registra visto / descartado / clic. Best-effort: el backend es idempotente y
 * un recibo perdido solo afecta las métricas (o vuelve a mostrar el aviso).
 * @param {string} id
 * @param {"seen"|"dismissed"|"cta_clicked"} event
 */
export async function sendAnnouncementReceipt(id, event) {
  try {
    await Axios.post(`${BASE}/${id}/receipts`, { event }, { timeout: PORTAL_TIMEOUT_MS });
    return true;
  } catch {
    return false;
  }
}

// ── Autores ──────────────────────────────────────────────────────────────────

export const listAnnouncements = (params) =>
  authorCall(() => Axios.get(BASE, { params }), "No se pudieron cargar los pop-ups.");

export const createAnnouncement = (body) =>
  authorCall(() => Axios.post(BASE, body), "No se pudo crear el pop-up.");

export const updateAnnouncement = (id, body) =>
  authorCall(() => Axios.patch(`${BASE}/${id}`, body), "No se pudo guardar el pop-up.");

export const publishAnnouncement = (id) =>
  authorCall(() => Axios.post(`${BASE}/${id}/publish`), "No se pudo publicar el pop-up.");

export const archiveAnnouncement = (id) =>
  authorCall(() => Axios.post(`${BASE}/${id}/archive`), "No se pudo archivar el pop-up.");

/** @param {{ scope?: string, logisticsCompanyId?: string, audience: object }} body */
export const previewAnnouncementAudience = (body) =>
  authorCall(
    () => Axios.post(`${BASE}/audience-preview`, body),
    "No se pudo calcular el alcance.",
  );

/** @param {{ scope?: string, logisticsCompanyId?: string }} params */
export const getAnnouncementAudienceOptions = (params) =>
  authorCall(
    () => Axios.get(`${BASE}/audience-options`, { params }),
    "No se pudieron cargar las integraciones.",
  );

export const getAnnouncementCommuneOptions = (countryCode) =>
  authorCall(
    () => Axios.get(`${BASE}/commune-options`, { params: { countryCode } }),
    "No se pudieron cargar las comunas.",
  );

/** Tipos de imagen que acepta el backend (sin SVG: puede llevar script). */
export const ANNOUNCEMENT_IMAGE_TYPES = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
};

/** Mismo tope que se valida antes de subir; el bucket es público. */
export const ANNOUNCEMENT_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Sube una imagen por el proxy de archivos (ms-s3-ots) y devuelve su URL.
 *
 * El nombre se rehace desde el MIME: ms-s3-ots conserva la extensión del
 * archivo original y el backend de anuncios exige una extensión de imagen. La
 * carpeta lleva un UUID para que la URL no se pueda adivinar.
 *
 * @param {File} file
 * @param {string} ownerKey id del operador o "platform"
 * @returns {Promise<{ ok: boolean, url?: string, error?: string }>}
 */
export async function uploadAnnouncementImage(file, ownerKey) {
  const extension = ANNOUNCEMENT_IMAGE_TYPES[file?.type];
  if (!extension) {
    return { ok: false, error: "Formato no permitido: usa PNG, JPG, WEBP o GIF." };
  }
  if (file.size > ANNOUNCEMENT_IMAGE_MAX_BYTES) {
    return { ok: false, error: "La imagen pesa más de 2 MB." };
  }
  if (typeof window.crypto?.randomUUID !== "function") {
    return { ok: false, error: "El navegador no permite subir imágenes desde esta página." };
  }
  const fd = new FormData();
  fd.append("files", file, `imagen.${extension}`);
  fd.append("dir", `announcements/${ownerKey}/${window.crypto.randomUUID()}`);
  try {
    const response = await Axios.post("/files/s/upload/img", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });
    // ms-s3-ots informa sus errores con un 2xx sin `files`.
    const location = response?.data?.files?.[0]?.location;
    if (typeof location !== "string" || !location) {
      return { ok: false, error: "La subida no devolvió la imagen." };
    }
    return { ok: true, url: location };
  } catch (err) {
    return { ok: false, error: apiErrorMessage(err, "No se pudo subir la imagen.") };
  }
}
